import React, { useState, useContext, useEffect } from 'react';
import logo from '../assets/images/logo.svg';
import { FaMapMarkerAlt, FaRegUserCircle, FaShoppingCart, FaTimes, FaEdit, FaBars } from 'react-icons/fa';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LuLogOut } from 'react-icons/lu';
import Config from '../API/Config';
import useLogout from '../utils/logout';
import { LocationContext } from '../context/LocationContext';
import { useCart } from '../context/CartContext';

const Header = () => {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const { location, setIsLocationModalOpen } = useContext(LocationContext);
    const IsLogin = Config.getUserIsLogin();
    const [profileOpen, setProfileOpen] = useState(false);
    const { cartCount, refreshCartCount } = useCart();

    useEffect(() => {
        refreshCartCount();
    }, [IsLogin, refreshCartCount]);

    const toggleSidebar = () => setSidebarOpen((prev) => !prev);

    const logout = useLogout();
    const navigate = useNavigate();
    const locationRouter = useLocation();

    const handleClick = () => {
        if (locationRouter.pathname === '/') {
            const section = document.getElementById('AppDownloadSection');
            if (section) {
                const headerOffset = 100;
                const elementPosition = section.getBoundingClientRect().top + window.scrollY;
                const offsetPosition = elementPosition - headerOffset;

                window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth',
                });
            }
        } else {
            navigate('/', { state: { scrollToApp: true } });
        }
    };

    const shortAddress = location ? (location.address) : (location.city);

    return (
        <>
            <header className="absolute top-0 left-0 w-full py-4 z-20 bg-transparent">
                <div className="container mx-auto flex justify-between items-center px-4 md:px-10">
                    {/* Logo */}
                    <Link to={'/'}>
                        <img src={logo} alt="Rotiey" className="w-44 md:w-52 h-auto object-contain" />
                    </Link>

                    {/* Desktop Navigation */}
                    <div className="hidden md:flex items-center space-x-6">
                        <nav className="flex space-x-6 text-white text-lg font-medium">
                            <Link to={'/start-delivering-rotiey'} className="hover:text-theme hidden lg:block">Start Earning</Link>
                            <Link to={'/become-a-rotiey-maker'} className="hover:text-theme hidden lg:block">Partner with us</Link>
                        </nav>

                        <button onClick={handleClick} className=" hidden lg:block bg-black text-white px-4 py-2 rounded-md text-lg font-medium">
                            Get the App
                        </button>

                        {!IsLogin && (
                            <Link to={`/sign-in`} className="border border-white text-white px-4 py-2 rounded-md hover:bg-theme2 font-medium">
                                Sign in
                            </Link>
                        )}

                        <div className="bg-white rounded-full flex items-center md:px-3 md:py-3 px-3 py-2 shadow-md w-48">
                            <FaMapMarkerAlt className="text-theme2 mr-2" />
                            <span className="flex-1 text-gray-600 text-sm pr-3 overflow-hidden text-ellipsis whitespace-nowrap">
                                {shortAddress}
                            </span>
                            <FaEdit className="text-theme2 cursor-pointer" onClick={() => setIsLocationModalOpen(true)} />
                        </div>

                        {/* Cart */}
                        <Link to={IsLogin ? `/cart` : `/sign-in`} className="relative text-white cursor-pointer">
                            <FaShoppingCart className="text-xl" />
                            {IsLogin && cartCount > 0 && (
                                <span className="absolute -top-2 -right-2 bg-red-600 text-xs px-1 rounded-full min-w-[18px] h-[18px] flex items-center justify-center">
                                    {cartCount}
                                </span>
                            )}
                        </Link>

                        {/* User Icon Dropdown */}
                        {IsLogin && (
                            <div className="relative group">
                                <div className="bg-white uppercase text-theme2 md:w-9 w-7 md:h-9 h-7 border-4 border-white flex items-center justify-center rounded-full md:text-3xl text-xl shadow-md">
                                    {Config.getUserName() ? Config.getUserName().charAt(0) : <FaRegUserCircle />}
                                </div>
                                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-30">
                                    <ul className="py-2">
                                        <li>
                                            <Link to="/profile" className="block px-4 py-2 text-darktext hover:text-theme2 transition">
                                                My Profile
                                            </Link>
                                        </li>
                                        <li>
                                            <Link to="/my-order" className="block px-4 py-2 text-darktext hover:text-theme2 transition">
                                                Orders Details
                                            </Link>
                                        </li>
                                        <hr />
                                        <li>
                                            <button onClick={logout} className="w-full text-left flex items-center px-4 py-2 text-theme2">
                                                <LuLogOut className="mr-2" /> Logout
                                            </button>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Mobile Location Icon */}
                    <div className='block md:hidden'>
                        <button
                            onClick={() => setIsLocationModalOpen(true)}
                            className="md:hidden mr-3 text-white text-2xl border border-white p-1.5 rounded"
                        >
                            <FaMapMarkerAlt />
                        </button>
                        <button
                            onClick={toggleSidebar}
                            className="lg:hidden text-white text-2xl border border-white p-1.5 rounded"
                        >
                            <FaBars />
                        </button>
                    </div>
                </div>

                {/* MOBILE SIDEBAR */}
                <div
                    className={`fixed top-0 right-0 h-full w-64 bg-white text-black shadow-lg transform transition-transform duration-300 z-30 
            ${sidebarOpen ? 'translate-x-0' : 'translate-x-full'}`}
                >
                    <div className="flex justify-between items-center p-4 border-b border-gray-200">
                        <h2 className="text-lg font-semibold">Menu</h2>
                        <button onClick={toggleSidebar} className="text-2xl">
                            <FaTimes />
                        </button>
                    </div>

                    <nav className="flex flex-col space-y-4 p-4 text-lg">
                        <Link to={'/start-delivering-rotiey'} className="hover:text-theme">Start Earning</Link>
                        <Link to={'/become-a-rotiey-maker'} className="hover:text-theme">Partner with us</Link>
                        <Link to={IsLogin ? `/profile` : `/sign-in`} className="hover:text-theme">{IsLogin ? `Profile` : `Sign In`}</Link>
                    </nav>
                </div>

                {/* DARK OVERLAY */}
                {sidebarOpen && <div onClick={toggleSidebar} className="fixed inset-0 bg-black bg-opacity-50 z-20"></div>}
            </header>
        </>
    );
};

export default Header;







// import React, { useState } from "react";
// import logo from "../assets/images/logo.svg";
// import { FaBars, FaRegUserCircle, FaShoppingCart, FaTimes } from "react-icons/fa";
// import { Link, useLocation, useNavigate } from "react-router-dom";
// import { LuLogOut } from "react-icons/lu";
// import Config from "../API/Config";
// import Swal from "sweetalert2";
// import useLogout from "../utils/logout";

// const Header = () => {
//     const [sidebarOpen, setSidebarOpen] = useState(false);
//     const IsLogin = Config.getUserIsLogin()
//     const [profileOpen, setProfileOpen] = useState(false);

//     const toggleSidebar = () => setSidebarOpen((prev) => !prev);

//     const logout = useLogout();

//     const navigate = useNavigate();
//     const location = useLocation();

//     const handleClick = () => {
//         if (location.pathname === "/") {
//             // Already on home → just scroll
//             const section = document.getElementById("AppDownloadSection");
//             if (section) {
//                 const headerOffset = 100; // adjust for fixed header
//                 const elementPosition = section.getBoundingClientRect().top + window.scrollY;
//                 const offsetPosition = elementPosition - headerOffset;

//                 window.scrollTo({
//                     top: offsetPosition,
//                     behavior: "smooth",
//                 });
//             }
//         } else {
//             // Go to home page, then scroll
//             navigate("/", { state: { scrollToApp: true } });
//         }
//     };

//     const UserName = Config.getUserName();
//     return (
//         <>
//             <header className="absolute top-0 left-0 w-full py-4 z-20 bg-transparent">
//                 <div className="container mx-auto flex justify-between items-center px-4 md:px-10">

//                     {/* Logo */}
//                     <Link to={'/'}>
//                         <img
//                             src={logo}
//                             alt="Rotiey"
//                             className="w-44 md:w-52 h-auto object-contain"
//                         />
//                     </Link>

//                     {/* Desktop Navigation */}
//                     <div className="hidden md:flex items-center space-x-6">
//                         <nav className="flex space-x-6 text-white text-lg font-medium">
//                             <Link to={'/partner'} className="hover:text-theme">Start Earning</Link>
//                             <Link to={'/partner'} className="hover:text-theme">Partner with us</Link>
//                         </nav>

//                         <button onClick={handleClick} className="bg-black text-white px-4 py-2 rounded-md text-lg font-medium">
//                             Get the App
//                         </button>

//                         {!IsLogin && (
//                             <Link to={`/sign-in`} className="border border-white text-white px-4 py-2 rounded-md hover:bg-theme2 font-medium">
//                                 Sign in
//                             </Link>
//                         )}

//                         {/* Cart */}
//                         <Link to={IsLogin ? `/profile` : `/cart`} className="relative text-white cursor-pointer">
//                             <FaShoppingCart className="text-xl" />
//                             <span className="absolute -top-2 -right-2 bg-red-600 text-xs px-1 rounded-full">
//                                 1
//                             </span>
//                         </Link>

//                         {/* User Icon Dropdown */}
//                         {IsLogin && (
//                             <div className="relative group">
//                                 <div className="bg-white uppercase text-theme2 md:w-9 w-7 md:h-9 h-7 border-4 border-white flex items-center justify-center rounded-full md:text-3xl text-xl shadow-md">
//                                     {UserName ? UserName.charAt(0) : <FaRegUserCircle />}
//                                 </div>
//                                 <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
//                                     <ul className="py-2">
//                                         <li>
//                                             <Link to="/profile" className="block px-4 py-2 text-darktext hover:text-theme2 transition">
//                                                 My Profile
//                                             </Link>
//                                         </li>
//                                         <li>
//                                             <Link to="/my-order" className="block px-4 py-2 text-darktext hover:text-theme2 transition">
//                                                 Orders Details
//                                             </Link>
//                                         </li>
//                                         <hr />
//                                         <li>
//                                             <button onClick={logout} className="w-full text-left flex items-center px-4 py-2 text-theme2">
//                                                 <LuLogOut className="mr-2" /> Logout
//                                             </button>
//                                         </li>
//                                     </ul>
//                                 </div>
//                             </div>
//                         )}
//                     </div>

//                     {/* Mobile Hamburger */}
//                     <button
//                         onClick={toggleSidebar}
//                         className="md:hidden text-white text-2xl border border-white p-1.5 rounded"
//                     >
//                         <FaBars />
//                     </button>
//                 </div>

//                 {/* MOBILE SIDEBAR */}
//                 <div
//                     className={`fixed top-0 right-0 h-full w-64 bg-white text-black shadow-lg transform transition-transform duration-300 z-30
//     ${sidebarOpen ? "translate-x-0" : "translate-x-full"}`}
//                 >
//                     <div className="flex justify-between items-center p-4 border-b border-gray-200">
//                         <h2 className="text-lg font-semibold">Menu</h2>
//                         <button onClick={toggleSidebar} className="text-2xl">
//                             <FaTimes />
//                         </button>
//                     </div>

//                     <nav className="flex flex-col space-y-4 p-4 text-lg">
//                         <Link to={'/partner'} className="hover:text-theme">Start Earning</Link>
//                         <Link to={'/partner'} className="hover:text-theme">Partner with us</Link>
//                         <Link to={IsLogin ? `/profile` : `/sign-in`} className="hover:text-theme">Sign In</Link>
//                     </nav>
//                 </div>

//                 {/* DARK OVERLAY */}
//                 {sidebarOpen && (
//                     <div onClick={toggleSidebar} className="fixed inset-0 bg-black bg-opacity-50 z-20"></div>
//                 )}
//             </header>

//         </>
//     );
// };

// export default Header;
